import React, { useMemo, useState, useCallback, useEffect } from "react";
import { AlertTriangle, RefreshCw, Activity, Droplet, Shield, Drill, Gauge, Zap, Clipboard } from "lucide-react";
import { toast } from "sonner";
import { getWellStatusSnapshot, formatSnapshotForText } from "@/lib/reports/wellStatusSnapshot";

// ============================================================================
// CurrentWellStatusPanel — read-only snapshot of the latest cross-report data
//
// Renders a 6-row card grid (DDR · Mud · BOP · Bit · MAASP · Pump) sourced
// from the existing report stores via the pure `getWellStatusSnapshot()`
// aggregator. The panel never mutates source data. The "Copy to Notes"
// button calls back into the parent form once when the user explicitly
// requests it.
// ============================================================================

const MIN_TOUCH = 44;

const ICONS = {
  ddr:         { Icon: Activity, color: "#34d399" },
  mud:         { Icon: Droplet, color: "#60a5fa" },
  bop:         { Icon: Shield, color: "#f5b400" },
  bit:         { Icon: Drill, color: "#fb923c" },
  wellControl: { Icon: Gauge, color: "#f87171" },
  pump:        { Icon: Zap, color: "#a78bfa" },
};

function StatusRow({ id, row }) {
  const { Icon, color } = ICONS[id] || { Icon: Activity, color: "#9aa6b2" };
  return (
    <div
      data-testid={`wstatus-row-${id}`}
      className="rounded-md border p-2.5 flex flex-col gap-1.5"
      style={{
        borderColor: row.warningText ? "rgba(248,113,113,0.45)" : "var(--rs-border,#1f2630)",
        background: "var(--rs-panel-2,#161b21)",
      }}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 shrink-0" style={{ color }} />
        <span className="text-[10px] uppercase tracking-[0.10em] text-[var(--rs-text-3,#6b7785)] font-semibold">
          {row.label}
        </span>
        {row.asOf && (
          <span
            data-testid={`wstatus-row-${id}-asof`}
            className="ml-auto text-[10px] font-mono-rs text-[var(--rs-text-3,#6b7785)]"
          >
            as of {row.asOf}
          </span>
        )}
      </div>
      <div
        data-testid={`wstatus-row-${id}-value`}
        className="text-[14px] font-mono-rs font-bold text-[var(--rs-text,#e8edf2)]"
      >
        {row.value != null ? row.value : <span className="text-[var(--rs-text-3,#6b7785)]">—</span>}
      </div>
      {row.extras && Object.keys(row.extras).length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] font-mono-rs text-[var(--rs-text-dim,#9aa6b2)]">
          {Object.entries(row.extras).map(([k, v]) => (
            <span key={k} data-testid={`wstatus-row-${id}-extra-${k.replace(/\s+/g, "-").toLowerCase()}`}>
              <span className="text-[var(--rs-text-3,#6b7785)]">{k}:</span> {v}
            </span>
          ))}
        </div>
      )}
      {row.warningText && (
        <div
          data-testid={`wstatus-row-${id}-warning`}
          className="text-[11px] text-[#fca5a5] leading-snug flex items-start gap-1.5 pt-0.5"
        >
          <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
          <span>{row.warningText}</span>
        </div>
      )}
    </div>
  );
}

export default function CurrentWellStatusPanel({ onCopyToNotes, autoPrefillEmpty }) {
  const [bump, setBump] = useState(0);
  const snapshot = useMemo(() => getWellStatusSnapshot(), [bump]);
  const formatted = useMemo(() => formatSnapshotForText(snapshot), [snapshot]);

  // One-shot auto-prefill (only when parent passes a callback AND the parent
  // signals the target field is empty). The parent guards the empty-check —
  // we just forward the formatted text once on mount.
  const ranAutoRef = React.useRef(false);
  useEffect(() => {
    if (ranAutoRef.current) return;
    ranAutoRef.current = true;
    if (typeof autoPrefillEmpty === "function" && formatted) {
      autoPrefillEmpty(formatted);
    }
  }, [formatted, autoPrefillEmpty]);

  const handleRefresh = useCallback(() => {
    setBump((b) => b + 1);
    toast.success("Snapshot refreshed");
  }, []);

  const handleCopy = useCallback(() => {
    if (!formatted) {
      toast.error("Nothing to copy yet");
      return;
    }
    if (typeof onCopyToNotes === "function") onCopyToNotes(formatted);
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        navigator.clipboard.writeText(formatted).catch(() => {});
      }
    } catch {
      // clipboard unavailable in some preview/PWA contexts — non-fatal
    }
    toast.success("Snapshot copied to Operations Summary");
  }, [formatted, onCopyToNotes]);

  const warnings = snapshot.warnings || [];
  const rowOrder = ["ddr", "mud", "bop", "bit", "wellControl", "pump"];

  return (
    <div data-testid="wstatus-panel" className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.12em] text-[var(--rs-amber,#f5b400)] font-semibold">
            Auto-Populated · Read-Only
          </div>
          <h3
            data-testid="wstatus-panel-title"
            className="text-[14px] font-bold text-[var(--rs-text,#e8edf2)] mt-0.5"
          >
            Current Well Status
          </h3>
          <p className="text-[10.5px] text-[var(--rs-text-dim,#9aa6b2)] mt-1 max-w-[60ch]">
            Auto-aggregated from your most recent saved drafts and calculator sheets.
            Source data is not modified. Verify against the rig tally and live readings before relying on this snapshot.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="wstatus-refresh"
            onClick={handleRefresh}
            className="btn-ghost rounded-md inline-flex items-center gap-1.5 text-[11px] label-up px-3"
            style={{ minHeight: MIN_TOUCH }}
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button
            type="button"
            data-testid="wstatus-copy"
            onClick={handleCopy}
            className="rounded-md inline-flex items-center gap-1.5 text-[11px] font-semibold px-3"
            style={{
              minHeight: MIN_TOUCH,
              background: "var(--rs-amber,#f5b400)",
              color: "#0a0b0e",
            }}
          >
            <Clipboard className="w-3.5 h-3.5" /> Copy to Notes
          </button>
        </div>
      </div>

      {warnings.length > 0 && (
        <div
          data-testid="wstatus-aggregate-warning"
          className="rounded-md border p-2.5 text-[11.5px] leading-snug flex items-start gap-2"
          style={{
            borderColor: "rgba(248,113,113,0.45)",
            background: "rgba(127,29,29,0.18)",
            color: "#fecaca",
          }}
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold mb-0.5">
              {warnings.length} watch item{warnings.length === 1 ? "" : "s"} detected
            </div>
            <ul className="list-disc pl-4 space-y-0.5">
              {warnings.map((w, i) => (
                <li key={i} data-testid={`wstatus-aggregate-warning-${i}`}>{w.replace(/^⚠️\s*/, "")}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {rowOrder.map((k) => (
          <StatusRow key={k} id={k} row={snapshot.rows[k]} />
        ))}
      </div>

      <div
        data-testid="wstatus-footer"
        className="text-[10px] font-mono-rs text-[var(--rs-text-3,#6b7785)] tracking-wide"
      >
        Generated {snapshot.generatedAt}
      </div>
    </div>
  );
}
